'use client'

import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ReportFormat } from '~/app/accounting/useGenerateReport'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrg } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'

export type AccountingReportStatus = 'generating' | 'ready' | 'failed'

export type AccountingReport = {
  id: string
  createdAt: string
  kind: string
  format: ReportFormat
  status: AccountingReportStatus
  periodLabel: string
  filtersLabel: string | null
  fileName: string
  downloadUrl: string | null
  failureReason: string | null
  createdByName: string | null
}

// Queued reports finish in a worker, so the list polls itself while any row is
// still running and settles once they've all landed.
const POLL_INTERVAL_MS = 3000

export const useAccountingReports = () => {
  const { org } = useActiveOrg()
  const { serverGetRequest } = useServerRequests()

  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.ORG_ACCOUNTING_REPORTS, org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const response = await serverGetRequest<{ reports: AccountingReport[] }>({
        path: '/api/org/accounting/reports',
        params: { orgId: org!.id },
        errorMessage: 'Failed to load reports',
      })
      return response?.data.reports ?? []
    },
    refetchInterval: (query) =>
      (query.state.data ?? []).some((report) => report.status === 'generating')
        ? POLL_INTERVAL_MS
        : false,
  })

  return { reports: data ?? [], isLoading }
}

// The server only deletes rows it reads as failed.
export const useDeleteAccountingReport = () => {
  const { org } = useActiveOrg()
  const queryClient = useQueryClient()
  const { serverDeleteRequest } = useServerRequests()

  return useCallback(
    async (reportId: string) => {
      if (!org?.id) return

      const result = await serverDeleteRequest({
        path: `/api/org/accounting/reports/${reportId}?orgId=${org.id}`,
        successMessage: 'Report deleted',
        errorMessage: 'Failed to delete report',
      })
      if (result) {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.ORG_ACCOUNTING_REPORTS, org.id],
        })
      }
    },
    [org?.id, queryClient, serverDeleteRequest],
  )
}
